import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as React from "react";
import {Editor, Range, Transforms, type MathEditorElement} from "slate";
import {ReactEditor, useSelected, useSlate} from "slate-react";

import type {AnalyticsEvent} from "@khanacademy/event-schemas";
import {
    createMathField,
    CursorContext,
    getCursorContext,
    getKeyTranslator,
    type MathFieldInterface,
} from "@khanacademy/math-input";
import type {
    AnalyticsEventHandlerFn,
    KeypadKey,
    PerseusAnalyticsEvent,
} from "@khanacademy/perseus-core";
import Clickable from "@khanacademy/wonder-blocks-clickable";
import {addStyle} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Popover} from "@khanacademy/wonder-blocks-popover";
import {useTimeout} from "@khanacademy/wonder-blocks-timing";
import {
    semanticColor,
    sizing,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {mathInputStrings} from "@khan/component-perseus";
import {
    useMathEditorAPIContext,
    useMathEditorContext,
} from "@khan/guide-math-editor-context";
import {RequestInfo} from "@khan/shared-core";
import {isMathEditor} from "@khan/slate-helpers";

import {isKhanmigoEditing} from "../slate/is-khanmigo-editing.ts";

import {MathEditorKeypad} from "./math-editor-keypad.tsx";

import caretDownIcon from "@phosphor-icons/core/bold/caret-down-bold.svg";
import caretUpIcon from "@phosphor-icons/core/bold/caret-up-bold.svg";

interface Props {
    element: MathEditorElement;
    children: React.ReactNode;
    attributes: any;

    // Whether the user can send a message when pressing enter in the math
    // editor.
    canSendMessage: boolean;
}

// The math editor for the AI Guide. Provides a math keypad for easy entry of
// math expressions.
export const MathEditor = (props: Props) => {
    const {children, attributes, element, canSendMessage} = props;

    const editor = useSlate();
    const selected = useSelected();

    const initialized = useRef(false);
    const [cursorContext, setCursorContext] = React.useState<
        (typeof CursorContext)[keyof typeof CursorContext]
    >(CursorContext.NONE);

    const updateCursorContext = useCallback(
        (mathField?: MathFieldInterface | null) => {
            if (mathField) {
                setCursorContext(getCursorContext(mathField));
            }
        },
        [],
    );

    const mathquillWrapper = useRef<HTMLSpanElement | null>(null);
    const mathField = useRef<MathFieldInterface | null>(null);
    const prevValue = useRef<string | null>(null);
    // This is a hack for LC-1158: when WB Popover is rendered at the same
    // time as the anchor element, the popover doesn't know where to position
    // itself. So we wait a beat after the anchor renders to render the popover.
    const [anchorMounted, setAnchorMounted] = useState<boolean>(false);
    const {set: delayedSetAnchorMounted} = useTimeout(
        () => setAnchorMounted(true),
        0,
    );

    const {setOpenKeypadID, setActiveInputs} = useMathEditorAPIContext();
    const {openKeypadID, sendMessage} = useMathEditorContext();

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const {id} = ReactEditor.findKey(editor, element);

    const closeKeypad = useCallback(() => {
        setOpenKeypadID(null);
    }, [setOpenKeypadID]);

    useEffect(() => {
        // Cursor is on this math input
        const {selection} = editor;
        if (selected && selection && Range.isCollapsed(selection)) {
            mathField.current?.focus();
        }
    }, [editor, selected]);

    const handleMoveOutOf = useCallback(
        (dir: -1 | 1) => {
            const {selection} = editor;
            if (selection && Range.isCollapsed(selection)) {
                Transforms.move(editor, {
                    reverse: dir === -1,
                });
                ReactEditor.focus(editor);
            }
        },
        [editor],
    );

    useEffect(() => {
        if (!isMathEditor(element)) {
            throw new Error("MathEditor must be used with a math element");
        }

        if (
            // If we haven't made a MathField
            !mathField.current &&
            // And we have somewhere to put it
            mathquillWrapper.current
        ) {
            mathField.current = createMathField(
                mathquillWrapper.current,
                RequestInfo.kaLocale,
                mathInputStrings(),
                (baseConfig) => ({
                    // Start with the default KA MathField config and extend it
                    ...baseConfig,

                    handlers: {
                        edit: (mathField) => {
                            // This handler is guaranteed to be called on change, but
                            // unlike React it sometimes generates false positives.
                            // One of these is on initialization (with an empty string
                            // value), so we have to guard against that below.
                            let value = mathField.latex();

                            // Provide a MathQuill-compatible way to generate the
                            // not-equals sign without pasting unicode or typing TeX
                            value = value.replace(/<>/g, "\\ne");

                            Transforms.setNodes<MathEditorElement>(
                                editor,
                                {prevTex: prevValue.current, tex: value},
                                {
                                    match: (node) =>
                                        ReactEditor.findKey(editor, node).id ===
                                        id,
                                },
                            );
                            updateCursorContext(mathField);

                            prevValue.current = value;
                        },
                        enter: () => {
                            if (canSendMessage) {
                                sendMessage();
                            }
                        },
                        // cannot find an idiomatic way to go "up" or "down" like
                        //   the native behavior of contenteditable; for now, we
                        //   just go left or right.
                        upOutOf: () => handleMoveOutOf(-1),
                        downOutOf: () => handleMoveOutOf(1),
                        // type of "dir" is wrong in types at time
                        //   of writing; remove case when fixed
                        //   https://github.com/Khan/perseus/pull/700
                        moveOutOf: (dir) => handleMoveOutOf(dir as any),
                        deleteOutOf: (dir) => handleMoveOutOf(dir as any),
                    },
                }),
            );
        }

        // Populates the math input with the selected text from a text input
        if (!initialized.current) {
            mathField.current?.latex(element.tex);

            if (!isKhanmigoEditing(editor)) {
                setOpenKeypadID(id);
            }

            initialized.current = true;
        }
    }, [
        children,
        editor,
        element,
        canSendMessage,
        sendMessage,
        handleMoveOutOf,
        id,
        initialized,
        setOpenKeypadID,
        updateCursorContext,
    ]);

    if (!isMathEditor(element)) {
        throw new Error("MathEditor must be used with a math element");
    }

    const keypadOpen = openKeypadID === id;

    const handleKeypadPress = useCallback((key: KeypadKey, e: any) => {
        const translator = getKeyTranslator(
            RequestInfo.kaLocale,
            mathInputStrings(),
        )[key];

        if (mathField.current && translator) {
            translator(mathField.current, key);
        }

        if (mathField.current) {
            setCursorContext(getCursorContext(mathField.current));
        }

        // We move focus back to the input when the keypad is clicked, so users
        // can freely alternate clicking and typing. However, we do not want to
        // move focus to the input if the device has an onscreen keyboard,
        // since focusing the input will probably show the keyboard and disrupt
        // the user's flow.
        //
        // Unfortunately, there is currently (2023) not a widely-supported way
        // to check if the current device has an onscreen keyboard, so we use
        // a hack: we only move the focus if the user is clicking with a mouse.
        // This will potentially do the wrong thing if the device has an
        // onscreen keyboard AND a mouse, but it should work for the majority
        // use-cases (desktop and mobile).
        //
        //   Clickable handles "onClick" differently than react;
        //   a keyboard event is "keydown" type.
        //   In react without WonderBlocks, "enter" or "space" keydown events
        //   are also "click" events, differentiated by "detail".
        if (
            e.type === "click" &&
            e.detail === 1 &&
            e.nativeEvent?.pointerType === "mouse"
        ) {
            mathField.current?.focus();
        }
    }, []);

    const onAnalyticsEvent = useCallback(
        async (
            e: Extract<
                PerseusAnalyticsEvent,
                {type: "math-input:keypad-opened" | "math-input:keypad-closed"}
            >,
        ) => {
            // Translate this into a CEDAR event and
            // publish it.
            const cedarEvent: AnalyticsEvent =
                e.type === "math-input:keypad-opened"
                    ? {
                          eventType: "MathInputVirtualKeypadOpened",
                          eventSchemaVersion: 3,
                          virtualKeypadVersion: e.payload.virtualKeypadVersion,
                          accessPoint: "aiGuide",
                      }
                    : {
                          eventType: "MathInputVirtualKeypadClosed",
                          eventSchemaVersion: 4,
                          virtualKeypadVersion: e.payload.virtualKeypadVersion,
                          accessPoint: "aiGuide",
                      };
            await publishAnalyticsEvent(cedarEvent);
        },
        [publishAnalyticsEvent],
    ) as AnalyticsEventHandlerFn;

    // Generate an aria label for screen readers. Adds sequential numbering
    // (1, 2, 3...) when multiple math editors are present for disambiguation.
    const ariaLabel = useMemo(() => {
        const mathEditorNodes = Array.from(
            Editor.nodes(editor, {
                at: [],
                match: (node) => isMathEditor(node),
            }),
        );

        if (mathEditorNodes.length <= 1) {
            return keypadOpen ? t`close math keypad` : t`open math keypad`;
        }

        const mathEditorIndex = mathEditorNodes.findIndex(
            ([node]) => node === element,
        );

        // We do not expect findIndex to return -1, but fall back to the
        // Slate node ID for robustness.
        const ariaLabelId = mathEditorIndex === -1 ? id : mathEditorIndex + 1;

        return keypadOpen
            ? t`close math keypad ${ariaLabelId}`
            : t`open math keypad ${ariaLabelId}`;
    }, [editor, element, keypadOpen, id]);

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error
        <StyledSpan
            onBlur={() => {
                setActiveInputs((prevState) => ({
                    ...prevState,
                    [id]: false,
                }));
            }}
            onFocus={() => {
                setActiveInputs((prevState) => ({
                    ...prevState,
                    [id]: true,
                }));
            }}
            style={[
                styles.mathquillInputWrapper,
                selected && styles.mathquillInputSelected,
            ]}
            // update cursor context on interaction
            onClick={() => updateCursorContext(mathField.current)}
            onKeyUp={() => updateCursorContext(mathField.current)}
            {...attributes}
        >
            <Popover
                opened={anchorMounted && openKeypadID === id}
                onClose={closeKeypad}
                dismissEnabled
                // Make sure that the props of the things rendered in the
                // content are properly memoized with useMemo and useCallback
                // to avoid causing extra renders that will then cause the
                // analytics event handler to be invoked extra times.
                content={() => (
                    <MathEditorKeypad
                        onClickKey={handleKeypadPress}
                        onAnalyticsEvent={onAnalyticsEvent}
                        cursorContext={cursorContext}
                        closeKeypad={closeKeypad}
                    />
                )}
            >
                <Clickable
                    aria-label={ariaLabel}
                    aria-haspopup="grid"
                    aria-pressed={keypadOpen}
                    onClick={() => setOpenKeypadID(keypadOpen ? null : id)}
                    style={styles.keypadToggleButton}
                    ref={() => {
                        // This is a hack for LC-1158: when WB Popover is rendered at the same
                        // time as the anchor element, the popover doesn't know where to position
                        // itself. So we wait a beat after the anchor renders to render the popover.
                        if (!anchorMounted) {
                            delayedSetAnchorMounted();
                        }
                    }}
                >
                    {({hovered, focused}) => (
                        <PhosphorIcon
                            icon={keypadOpen ? caretDownIcon : caretUpIcon}
                            size="small"
                            color={
                                hovered || focused
                                    ? semanticColor.core.foreground.neutral
                                          .strong
                                    : semanticColor.link.rest
                            }
                        />
                    )}
                </Clickable>
            </Popover>
            <StyledSpan style={styles.separator} />
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- TODO: Address a11y error */}
            <StyledSpan
                id={id}
                ref={mathquillWrapper}
                style={styles.mathquillInput}
                aria-label="math input"
                role="textbox"
                onBlur={() => mathField.current?.blur()}
                onFocus={() => mathField.current?.focus()}
                onClick={(e) => {
                    // Stop the click from being registered so that the
                    // keypad popover doesn't close when the user clicks
                    // on the input field.
                    e.stopPropagation();
                }}
            />
            {children}
        </StyledSpan>
    );
};

const StyledSpan = addStyle("span");

const blue16 = "#d2e0f9";
const attention = {
    boxShadow: `inset 0 0 0 ${sizing.size_020} ${semanticColor.core.border.instructive.default}`,
};
const styles = StyleSheet.create({
    mathquillInput: {
        display: "inline-flex",
        alignItems: "center",
        padding: spacing.xxxxSmall_2,
        boxShadow: "none",
        border: "none",
        borderRadius: 5,
        backgroundColor: "none",
        fontSize: 14,
        minInlineSize: 24,
    },
    mathquillInputWrapper: {
        display: "inline-flex",
        alignItems: "stretch",
        backgroundColor: "#f7f8fa",
        boxShadow: `inset 0 0 0 ${sizing.size_010} ${semanticColor.core.border.neutral.default}`,
        boxSizing: "border-box",
        borderRadius: 5,
        width: "fit-content",
        height: "fit-content",
        ":focus-within": attention,
        ":hover": attention,
    },
    mathquillInputSelected: attention,
    keypadToggleButton: {
        margin: 3,
        borderRadius: 3,
        width: 24,
        minBlockSize: 24,
        ":hover": {
            background: blue16,
        },
        ":focus": {
            background: blue16,
        },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    separator: {
        margin: "6px 0",
        borderLeftWidth: 1,
        borderStyle: "solid",
        borderColor: blue16,
    },
});
